import React, { useEffect, useState } from "react";
import { IonPage, IonContent, IonHeader, IonToolbar, useIonViewWillEnter } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStudent } from "../contexts/StudentContext";
import { getCatalog } from "../services/contentService";
import { subscribeActivity, DayActivity, xpForLevel } from "../services/progressService";
import { checkBadges } from "../services/badges";
import type { Catalog } from "../content/types";
import { subjectCompletion, chapterToReview, lastChapter } from "../utils/progressStats";
import { dueReviews } from "../services/reviewService";
import { dayKey } from "../utils/dates";
import ProgressBar from "../components/ProgressBar";
import RewardToast, { Reward } from "../components/RewardToast";

/**
 * Tableau de bord élève (PDF §13) : « Qu'est-ce que je dois travailler ? »
 * Objectif du jour, série, XP, progression par matière, révisions dues (répétition espacée)
 * ou, à défaut, chapitre à revoir / à continuer.
 */
const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { user, profile, stats, progress } = useStudent();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [today, setToday] = useState<DayActivity | null>(null);
  const [reward, setReward] = useState<Reward | null>(null);

  useEffect(() => {
    getCatalog().then(setCatalog).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!user) return;
    return subscribeActivity(user.uid, (a) => setToday(a[dayKey()] || null));
  }, [user]);

  // Vérification des badges à chaque retour sur l'accueil (léger, idempotent).
  useIonViewWillEnter(() => {
    if (!user) return;
    checkBadges(user.uid, stats).then((fresh) => {
      if (fresh.length) setReward({ icon: fresh[0].icon, message: t("rewards.badge", { name: t(fresh[0].nameKey) }), color: "warning" });
    });
  });

  if (!profile) return null;
  const year = profile.year;
  const minutesToday = Math.round((today?.seconds || 0) / 60);
  const goal = profile.dailyGoalMinutes || 15;
  const goalPct = Math.min(100, Math.round((minutesToday / goal) * 100));
  const nextLevelXp = xpForLevel(stats.level + 1);
  const prevLevelXp = xpForLevel(stats.level);
  const levelPct = Math.round(((stats.xp - prevLevelXp) / Math.max(1, nextLevelXp - prevLevelXp)) * 100);

  const due = catalog ? dueReviews(catalog, progress, year) : [];
  const review = catalog && due.length === 0 ? chapterToReview(catalog, progress, year) : null;
  const last = catalog ? lastChapter(catalog, progress) : null;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t("dashboard.morning") : hour < 18 ? t("dashboard.afternoon") : t("dashboard.evening");

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <div className="page-container flex items-center justify-between px-4 py-1">
            <div>
              <p className="text-xs text-gray-400">{greeting}</p>
              <h1 className="text-xl font-black text-white">
                {profile.avatar} {profile.displayName}
              </h1>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1 text-orange-300 font-bold" title={t("dashboard.streak")}>
                <i className="fa-solid fa-fire" /> {stats.streak.current}
              </span>
              <span className="flex items-center gap-1 text-accent-300 font-bold" title="XP">
                <i className="fa-solid fa-bolt" /> {stats.xp}
              </span>
            </div>
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <RewardToast reward={reward} onDismiss={() => setReward(null)} />
        <div className="page-container wide px-4 py-3 animate-fade-in lg:grid lg:grid-cols-[1.15fr_1fr] lg:gap-5 lg:items-start space-y-4 lg:space-y-0">
          {user?.isAnonymous && (
            <button type="button" onClick={() => history.push("/home/account")} className="w-full text-left card p-4 border-accent-500/40 bg-accent-500/10 active:scale-[0.99] transition">
              <p className="font-bold text-white">
                <i className="fa-solid fa-user-secret mr-2 text-accent-300" />
                {t("auth.guest.bannerTitle")}
              </p>
              <p className="text-xs text-gray-300 mt-1">{t("auth.guest.bannerBody")}</p>
              <p className="text-xs font-bold text-accent-300 mt-2">{t("auth.guest.dashboardCta")} →</p>
            </button>
          )}

          <div className="space-y-4 lg:contents">
          {/* Objectif du jour */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-white flex items-center gap-2">
                <i className="fa-solid fa-bullseye text-success-400" /> {t("dashboard.dailyGoal")}
              </span>
              <span className="text-sm text-gray-300">
                {minutesToday} / {goal} min
              </span>
            </div>
            <ProgressBar value={goalPct} color="linear-gradient(90deg,#10b964,#06b6d4)" />
            <p className="text-xs text-gray-400 mt-2">{goalPct >= 100 ? t("dashboard.goalDone") : t("dashboard.goalHint", { min: Math.max(0, goal - minutesToday) })}</p>
          </div>

          {/* Niveau */}
          <div className="card p-4 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center text-white font-black text-xl shadow-glow-primary">
              {stats.level}
            </div>
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-bold text-white">{t("dashboard.level", { level: stats.level })}</span>
                <span className="text-gray-400">
                  {stats.xp} / {nextLevelXp} XP
                </span>
              </div>
              <ProgressBar value={levelPct} />
            </div>
          </div>

          {/* Révisions espacées dues (PDF §1, §13) */}
          {due.length > 0 && (
            <div className="card p-4 border-primary-500/40 bg-primary-500/10 space-y-3">
              <p className="text-xs uppercase tracking-wider text-primary-300 font-bold flex items-center gap-2">
                <i className="fa-solid fa-brain" /> {t("dashboard.toReview")}
              </p>
              {due.map((d) => (
                <div key={d.chapter.id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold truncate">{d.chapter.title}</p>
                    <p className="text-xs text-gray-400">{d.overdueDays > 0 ? t("review.overdue", { count: d.overdueDays }) : t("review.dueToday")}</p>
                  </div>
                  <button type="button" onClick={() => history.push(`/home/chapter/${d.chapter.id}/review`)} className="btn-primary text-sm px-4 py-2 flex-shrink-0">
                    {t("dashboard.reviewCta")}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* À revoir / continuer (aucune révision due) */}
          {due.length === 0 && (review || last) && (
            <button
              type="button"
              onClick={() => history.push(`/home/chapter/${(review || last)!.id}`)}
              className="w-full text-left card p-4 border-primary-500/40 bg-primary-500/10 active:scale-[0.99] transition">
              <p className="text-xs uppercase tracking-wider text-primary-300 font-bold mb-1">
                {review ? t("dashboard.toReview") : t("dashboard.continue")}
              </p>
              <p className="text-white font-bold text-lg">{(review || last)!.title}</p>
              <p className="text-xs text-gray-400 mt-1">
                {t("dashboard.completion", { pct: progress[(review || last)!.id]?.completion || 0 })}
              </p>
            </button>
          )}

          </div>
          {/* Matières */}
          <div className="lg:col-start-2 lg:row-start-1 lg:row-span-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-bold text-white">{t("dashboard.subjects", { year })}</h2>
              <button type="button" onClick={() => history.push("/home/subjects")} className="text-xs text-primary-300 font-semibold">
                {t("common.seeAll")}
              </button>
            </div>
            <div className="space-y-2 md:grid md:grid-cols-2 md:gap-3 md:space-y-0 lg:block lg:space-y-2">
              {catalog?.subjects.map((s) => {
                const pct = subjectCompletion(catalog, progress, s.id, year);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => history.push(`/home/subject/${s.id}`)}
                    className="w-full card p-3 flex items-center gap-3 active:scale-[0.99] transition text-left">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: s.color }}>
                      <i className={s.icon} />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-semibold text-white">{s.name}</span>
                        <span className="text-gray-300">{pct} %</span>
                      </div>
                      <ProgressBar value={pct} color={s.color} height={6} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {!last && (
            <div className="card p-5 text-center">
              <div className="text-5xl mb-2 animate-float">🚀</div>
              <p className="text-white font-bold mb-1">{t("dashboard.emptyTitle")}</p>
              <p className="text-sm text-gray-400 mb-4">{t("dashboard.emptyBody")}</p>
              <button type="button" onClick={() => history.push("/home/subjects")} className="btn-primary w-full">
                {t("dashboard.emptyCta")}
              </button>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Dashboard;
